import { useState, useEffect } from "react";
import clsx from "clsx";

import { KEYS, capitalizeString } from "@excalidraw/common";

import { getElementAbsoluteCoords } from "@excalidraw/element";

import * as Popover from "@radix-ui/react-popover";

import { trackEvent } from "../analytics";

import { t } from "../i18n";

import { actionClearCanvas } from "../actions";
import { useSetAtom } from "../editor-jotai";

import { isHandToolActive } from "../appState";

import { centerScrollOn } from "../scene/scroll";

import { isColorDark } from "./ColorPicker/colorPickerUtils";

import { HandButton } from "./HandButton";
import { ToolButton } from "./ToolButton";
import DropdownMenu from "./dropdownMenu/DropdownMenu";
import { ToolPopover } from "./ToolPopover";
import { PropertiesPopover } from "./PropertiesPopover";
import { activeConfirmDialogAtom } from "./ActiveConfirmDialog";

import {
  SelectionIcon,
  CursorMoveIcon,
  FreedrawIcon,
  EraserIcon,
  RectangleIcon,
  ArrowIcon,
  extraToolsIcon,
  EllipseIcon,
  LineIcon,
  TextIcon,
  ImageIcon,
  frameToolIcon,
  EmbedIcon,
  laserPointerToolIcon,
  LassoIcon,
  HomeIcon,
  TrashIcon,
} from "./icons";

import "./ToolIcon.scss";
import "./MobileToolBar.scss";

import type {
  AppClassProperties,
  ToolType,
  UIAppState,
  AppState,
} from "../types";

const SHAPE_TOOLS = [
  {
    type: "rectangle",
    icon: RectangleIcon,
    title: capitalizeString(t("toolBar.rectangle")),
  },
  {
    type: "ellipse",
    icon: EllipseIcon,
    title: capitalizeString(t("toolBar.ellipse")),
  },
] as const;

const SELECTION_TOOLS = [
  {
    type: "selection",
    icon: SelectionIcon,
    title: capitalizeString(t("toolBar.selection")),
  },
  {
    type: "lasso",
    icon: LassoIcon,
    title: capitalizeString(t("toolBar.lasso")),
  },
] as const;

const LINEAR_ELEMENT_TOOLS = [
  {
    type: "arrow",
    icon: ArrowIcon,
    title: capitalizeString(t("toolBar.arrow")),
  },
  { type: "line", icon: LineIcon, title: capitalizeString(t("toolBar.line")) },
] as const;

type MobileToolBarProps = {
  app: AppClassProperties;
  onHandToolToggle: () => void;
  setAppState: React.Component<any, AppState>["setState"];
};

export const MobileToolBar = ({
  app,
  onHandToolToggle,
  setAppState,
}: MobileToolBarProps) => {
  const setActiveConfirmDialog = useSetAtom(activeConfirmDialogAtom);
  const activeTool = app.state.activeTool;
  const hasCanvasElements = app.scene.getNonDeletedElements().length > 0;
  const [isPencilOptionsOpen, setIsPencilOptionsOpen] = useState(false);
  const [isOtherShapesMenuOpen, setIsOtherShapesMenuOpen] = useState(false);
  const [lastActiveGenericShape, setLastActiveGenericShape] = useState<
    "rectangle" | "ellipse"
  >("rectangle");
  const [lastActiveLinearElement, setLastActiveLinearElement] = useState<
    "arrow" | "line"
  >("arrow");

  // keep lastActiveGenericShape in sync with active tool if user switches via other UI
  useEffect(() => {
    if (activeTool.type === "rectangle" || activeTool.type === "ellipse") {
      setLastActiveGenericShape(activeTool.type);
    }
  }, [activeTool.type]);

  // keep lastActiveLinearElement in sync with active tool if user switches via other UI
  useEffect(() => {
    if (activeTool.type === "arrow" || activeTool.type === "line") {
      setLastActiveLinearElement(activeTool.type);
    }
  }, [activeTool.type]);

  const frameToolSelected = activeTool.type === "frame";
  const laserToolSelected = activeTool.type === "laser";
  const embeddableToolSelected = activeTool.type === "embeddable";

  const DRAW_GROUP_TOOL_TYPES = new Set([
    "freedraw",
    "rectangle",
    "diamond",
    "ellipse",
    "arrow",
    "line",
    "text",
  ] as const);

  const onHome = () => {
    const firstElement = app.scene.getNonDeletedElements()[0];
    if (firstElement) {
      const [x1, y1, x2, y2] = getElementAbsoluteCoords(
        firstElement,
        app.scene.getNonDeletedElementsMap(),
      );
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;
      setAppState(
        centerScrollOn({
          scenePoint: { x: centerX, y: centerY },
          viewportDimensions: {
            width: app.state.width,
            height: app.state.height,
          },
          zoom: app.state.zoom,
        }),
      );
      return;
    }

    setAppState({ scrollX: 0, scrollY: 0 });
  };

  const handleToolChange = (toolType: string, pointerType?: string) => {
    if (app.state.activeTool.type !== toolType) {
      trackEvent("toolbar", toolType, "ui");
    }

    if (toolType === "selection") {
      if (app.state.activeTool.type === "selection") {
        // Toggle selection tool behavior if needed
      } else {
        app.setActiveTool({ type: "selection" });
      }
    } else {
      app.setActiveTool({ type: toolType as ToolType });
    }

    if (!DRAW_GROUP_TOOL_TYPES.has(toolType as any)) {
      setIsPencilOptionsOpen(false);
    }
  };

  useEffect(() => {
    const unsubscribe = app.onPointerDownEmitter.on(() => {
      setIsPencilOptionsOpen(false);
    });
    return () => unsubscribe?.();
  }, [app]);

  const [toolbarWidth, setToolbarWidth] = useState(0);

  const [buttonSize, setButtonSize] = useState(36);
  const GAP = 4;

  const parseCssLengthToPx = (value: string, el: HTMLElement) => {
    const raw = value.trim();
    if (!raw) {
      return null;
    }
    const numeric = parseFloat(raw);
    if (!Number.isFinite(numeric)) {
      return null;
    }

    if (raw.endsWith("px")) {
      return numeric;
    }
    if (raw.endsWith("rem")) {
      const rootFontSize = parseFloat(
        window.getComputedStyle(document.documentElement).fontSize,
      );
      return numeric * (Number.isFinite(rootFontSize) ? rootFontSize : 16);
    }
    if (raw.endsWith("em")) {
      const fontSize = parseFloat(window.getComputedStyle(el).fontSize);
      return numeric * (Number.isFinite(fontSize) ? fontSize : 16);
    }

    // Unknown unit; try treating it as px.
    return numeric;
  };

  const WIDTH = buttonSize;

  // hand, selection, freedraw, eraser, rectangle, arrow, others
  const MIN_TOOLS = 7;
  const MIN_WIDTH = MIN_TOOLS * WIDTH + (MIN_TOOLS - 1) * GAP;
  const ADDITIONAL_WIDTH = WIDTH + GAP;

  const showTextToolOutside = toolbarWidth >= MIN_WIDTH + 1 * ADDITIONAL_WIDTH;
  const showImageToolOutside = toolbarWidth >= MIN_WIDTH + 2 * ADDITIONAL_WIDTH;
  const showFrameToolOutside = toolbarWidth >= MIN_WIDTH + 3 * ADDITIONAL_WIDTH;

  const extraTools = ["text", "image", "frame", "embeddable", "laser"].filter(
    (tool) => {
      if (showTextToolOutside && tool === "text") {
        return false;
      }
      if (showImageToolOutside && tool === "image") {
        return false;
      }
      if (showFrameToolOutside && tool === "frame") {
        return false;
      }
      return true;
    },
  );
  const extraToolSelected = extraTools.includes(activeTool.type);
  const extraIcon = extraToolSelected
    ? activeTool.type === "text"
      ? TextIcon
      : activeTool.type === "image"
      ? ImageIcon
      : activeTool.type === "frame"
      ? frameToolIcon
      : activeTool.type === "embeddable"
      ? EmbedIcon
      : activeTool.type === "laser"
      ? laserPointerToolIcon
      : extraToolsIcon
    : extraToolsIcon;

  return (
    <div
      className="mobile-toolbar"
      ref={(div) => {
        if (div) {
          setToolbarWidth(div.getBoundingClientRect().width);

          const cssVar = window
            .getComputedStyle(div)
            .getPropertyValue("--lg-button-size");
          const px = parseCssLengthToPx(cssVar, div);
          if (px) {
            setButtonSize(px);
          }
        }
      }}
    >
      {/* Home */}
      <ToolButton
        type="button"
        icon={HomeIcon}
        aria-label={capitalizeString(t("buttons.home"))}
        title={t("buttons.home")}
        onClick={onHome}
      />

      {/* Hand Tool */}
      <HandButton
        checked={isHandToolActive(app.state)}
        onChange={onHandToolToggle}
        title={t("toolBar.hand")}
        isMobile
      />

      {/* Clear Canvas */}
      {app.actionManager.isActionEnabled(actionClearCanvas) && (
        <ToolButton
          type="button"
          icon={TrashIcon}
          aria-label={t("buttons.clearReset")}
          title={t("buttons.clearReset")}
          onClick={() => setActiveConfirmDialog("clearCanvas")}
        />
      )}

      {/* Image */}
      {showImageToolOutside && (
        <ToolButton
          className={clsx({
            active: activeTool.type === "image",
          })}
          type="radio"
          icon={ImageIcon}
          checked={activeTool.type === "image"}
          name="editor-current-shape"
          title={`${capitalizeString(t("toolBar.image"))}`}
          aria-label={capitalizeString(t("toolBar.image"))}
          data-testid="toolbar-image"
          onClick={(event) => {
            if (activeTool.type === "image") {
              event.preventDefault();
              event.stopPropagation();
              app.setActiveTool({ type: "image" });
            }
          }}
          onChange={() => handleToolChange("image")}
        />
      )}

      {/* Selection Tool */}
      <ToolPopover
        app={app}
        options={SELECTION_TOOLS}
        activeTool={activeTool}
        defaultOption={app.state.preferredSelectionTool.type}
        namePrefix="selectionType"
        title={capitalizeString(t("toolBar.selection"))}
        data-testid="toolbar-selection"
        onToolChange={(type: string) => {
          if (type === "selection" || type === "lasso") {
            app.setActiveTool({ type });
            setAppState({
              preferredSelectionTool: { type, initialized: true },
            });
          }
        }}
        displayedOption={(() => {
          const preferred =
            SELECTION_TOOLS.find(
              (tool) => tool.type === app.state.preferredSelectionTool.type,
            ) || SELECTION_TOOLS[0];

          if (preferred.type === "selection" && hasCanvasElements) {
            return { ...preferred, icon: CursorMoveIcon };
          }

          return preferred;
        })()}
      />

      {/* Free Draw */}
      <Popover.Root
        open={isPencilOptionsOpen}
        onOpenChange={(open) => setIsPencilOptionsOpen(open)}
      >
        <Popover.Anchor asChild>
          <ToolButton
            className={clsx("ToolIcon--pencilColor", {
              active: DRAW_GROUP_TOOL_TYPES.has(activeTool.type as any),
            })}
            style={
              app.state.currentItemStrokeColor &&
              app.state.currentItemStrokeColor !== "transparent"
                ? ({
                    ["--pencil-active-bg" as any]:
                      app.state.currentItemStrokeColor,
                    ["--pencil-active-icon" as any]: isColorDark(
                      app.state.currentItemStrokeColor,
                    )
                      ? "#fff"
                      : "#000",
                  } as React.CSSProperties)
                : undefined
            }
            type="radio"
            icon={FreedrawIcon}
            checked={DRAW_GROUP_TOOL_TYPES.has(activeTool.type as any)}
            name="editor-current-shape"
            title={`${capitalizeString(t("toolBar.freedraw"))}`}
            aria-label={capitalizeString(t("toolBar.freedraw"))}
            data-testid="toolbar-freedraw"
            onClick={(event) => {
              if (DRAW_GROUP_TOOL_TYPES.has(activeTool.type as any)) {
                event.preventDefault();
                event.stopPropagation();
                setIsPencilOptionsOpen((v) => !v);
              }
            }}
            onChange={() => handleToolChange("freedraw")}
          />
        </Popover.Anchor>

        {isPencilOptionsOpen && (
          <PropertiesPopover
            className="properties-popup pencil-properties-popover"
            container={app.excalidrawContainerValue.container}
            side="bottom"
            align="center"
            sideOffset={8}
            alignOffset={0}
            onPointerDownOutside={(event) => {
              // Keep this popover open when interacting with other UI
              // (e.g. nested popovers like color pickers). We close the
              // pencil popover only on canvas pointer-down.
              event.preventDefault();
            }}
            onFocusOutside={(event) => {
              event.preventDefault();
            }}
            onClose={() => setIsPencilOptionsOpen(false)}
          >
            <div className="selected-shape-actions">
              <fieldset>
                <legend>Shape</legend>
                <div className="buttonList">
                  <ToolButton
                    type="radio"
                    icon={RectangleIcon}
                    checked={activeTool.type === "rectangle"}
                    name="pencil-shape"
                    aria-label={capitalizeString(t("toolBar.rectangle"))}
                    title={capitalizeString(t("toolBar.rectangle"))}
                    size="small"
                    onClick={(event) => {
                      if (activeTool.type === "rectangle") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToolChange("freedraw");
                      }
                    }}
                    onChange={() => handleToolChange("rectangle")}
                  />
                  <ToolButton
                    type="radio"
                    icon={EllipseIcon}
                    checked={activeTool.type === "ellipse"}
                    name="pencil-shape"
                    aria-label={capitalizeString(t("toolBar.ellipse"))}
                    title={capitalizeString(t("toolBar.ellipse"))}
                    size="small"
                    onClick={(event) => {
                      if (activeTool.type === "ellipse") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToolChange("freedraw");
                      }
                    }}
                    onChange={() => handleToolChange("ellipse")}
                  />
                  <ToolButton
                    type="radio"
                    icon={ArrowIcon}
                    checked={activeTool.type === "arrow"}
                    name="pencil-shape"
                    aria-label={capitalizeString(t("toolBar.arrow"))}
                    title={capitalizeString(t("toolBar.arrow"))}
                    size="small"
                    onClick={(event) => {
                      if (activeTool.type === "arrow") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToolChange("freedraw");
                      }
                    }}
                    onChange={() => handleToolChange("arrow")}
                  />
                  <ToolButton
                    type="radio"
                    icon={LineIcon}
                    checked={activeTool.type === "line"}
                    name="pencil-shape"
                    aria-label={capitalizeString(t("toolBar.line"))}
                    title={capitalizeString(t("toolBar.line"))}
                    size="small"
                    onClick={(event) => {
                      if (activeTool.type === "line") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToolChange("freedraw");
                      }
                    }}
                    onChange={() => handleToolChange("line")}
                  />
                  <ToolButton
                    type="radio"
                    icon={TextIcon}
                    checked={activeTool.type === "text"}
                    name="pencil-shape"
                    aria-label={capitalizeString(t("toolBar.text"))}
                    title={capitalizeString(t("toolBar.text"))}
                    size="small"
                    onClick={(event) => {
                      if (activeTool.type === "text") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToolChange("freedraw");
                      }
                    }}
                    onChange={() => handleToolChange("text")}
                  />
                </div>
              </fieldset>

              {app.actionManager.renderAction("changeStrokeColor")}
              {app.actionManager.renderAction("changeBackgroundColor")}
              {app.actionManager.renderAction("changeStrokeWidth")}
              {app.actionManager.renderAction("changeStrokeShape")}
              {app.actionManager.renderAction("changeOpacity")}
            </div>
          </PropertiesPopover>
        )}
      </Popover.Root>

      {/* Eraser */}
      <ToolButton
        className={clsx({
          active: activeTool.type === "eraser",
        })}
        type="radio"
        icon={EraserIcon}
        checked={activeTool.type === "eraser"}
        name="editor-current-shape"
        title={`${capitalizeString(t("toolBar.eraser"))}`}
        aria-label={capitalizeString(t("toolBar.eraser"))}
        data-testid="toolbar-eraser"
        onChange={() => handleToolChange("eraser")}
      />

      {/* Rectangle */}
      <ToolPopover
        app={app}
        options={SHAPE_TOOLS}
        activeTool={activeTool}
        defaultOption={lastActiveGenericShape}
        namePrefix="shapeType"
        title={capitalizeString(
          t(
            lastActiveGenericShape === "ellipse"
              ? "toolBar.ellipse"
              : "toolBar.rectangle",
          ),
        )}
        data-testid="toolbar-rectangle"
        fillable={true}
        onToolChange={(type: string) => {
          if (type === "rectangle" || type === "ellipse") {
            setLastActiveGenericShape(type);
            app.setActiveTool({ type });
          }
        }}
        displayedOption={
          SHAPE_TOOLS.find((tool) => tool.type === lastActiveGenericShape) ||
          SHAPE_TOOLS[0]
        }
      />

      {/* Arrow/Line */}
      <ToolPopover
        app={app}
        options={LINEAR_ELEMENT_TOOLS}
        activeTool={activeTool}
        defaultOption={lastActiveLinearElement}
        namePrefix="linearElementType"
        title={capitalizeString(
          t(
            lastActiveLinearElement === "arrow"
              ? "toolBar.arrow"
              : "toolBar.line",
          ),
        )}
        data-testid="toolbar-arrow"
        fillable={true}
        onToolChange={(type: string) => {
          if (type === "arrow" || type === "line") {
            setLastActiveLinearElement(type);
            app.setActiveTool({ type });
          }
        }}
        displayedOption={
          LINEAR_ELEMENT_TOOLS.find(
            (tool) => tool.type === lastActiveLinearElement,
          ) || LINEAR_ELEMENT_TOOLS[0]
        }
      />

      {/* Frame Tool */}
      {showFrameToolOutside && (
        <ToolButton
          className={clsx({ active: frameToolSelected })}
          type="radio"
          icon={frameToolIcon}
          checked={frameToolSelected}
          name="editor-current-shape"
          title={`${capitalizeString(t("toolBar.frame"))}`}
          aria-label={capitalizeString(t("toolBar.frame"))}
          data-testid="toolbar-frame"
          onChange={() => handleToolChange("frame")}
        />
      )}

      {/* Other Shapes */}
      <DropdownMenu open={isOtherShapesMenuOpen} placement="top">
        <DropdownMenu.Trigger
          className={clsx(
            "App-toolbar__extra-tools-trigger App-toolbar__extra-tools-trigger--mobile",
            {
              "App-toolbar__extra-tools-trigger--selected":
                extraToolSelected || isOtherShapesMenuOpen,
            },
          )}
          onToggle={() => {
            setIsOtherShapesMenuOpen(!isOtherShapesMenuOpen);
            setAppState({ openMenu: null, openPopup: null });
          }}
          title={t("toolBar.extraTools")}
          style={{
            width: WIDTH,
            height: WIDTH,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {extraIcon}
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          onClickOutside={() => setIsOtherShapesMenuOpen(false)}
          onSelect={() => setIsOtherShapesMenuOpen(false)}
          className="App-toolbar__extra-tools-dropdown"
        >
          {!showImageToolOutside && (
            <DropdownMenu.Item
              onSelect={() => app.setActiveTool({ type: "image" })}
              icon={ImageIcon}
              data-testid="toolbar-image"
              selected={activeTool.type === "image"}
            >
              {t("toolBar.image")}
            </DropdownMenu.Item>
          )}
          {!showFrameToolOutside && (
            <DropdownMenu.Item
              onSelect={() => app.setActiveTool({ type: "frame" })}
              icon={frameToolIcon}
              shortcut={KEYS.F.toLocaleUpperCase()}
              data-testid="toolbar-frame"
              selected={frameToolSelected}
            >
              {t("toolBar.frame")}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item
            onSelect={() => app.setActiveTool({ type: "embeddable" })}
            icon={EmbedIcon}
            data-testid="toolbar-embeddable"
            selected={embeddableToolSelected}
          >
            {t("toolBar.embeddable")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => app.setActiveTool({ type: "laser" })}
            icon={laserPointerToolIcon}
            data-testid="toolbar-laser"
            selected={laserToolSelected}
            shortcut={KEYS.K.toLocaleUpperCase()}
          >
            {t("toolBar.laser")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};
